import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useWorkorderContext } from "../../../../context/WorkorderContext";
import {
  useWorkorderPermissions,
  WorkorderField,
} from "../../../../hooks/useWorkorderPermissions";
import toast from "react-hot-toast";
import { getCookie } from "../../../../services/auth/cookies";
import { api } from "../../../../services/api/api";
import { showOdooSuccessToast } from "../../../../components/toast/DismissibleToast";

// Imports
import { TabsNav } from "../../../../components/tabs/TabsNav";
import { Informations } from "../informations/Informations";
import { Files } from "../files/Files";
import ExportPdfModal from "../../../../components/export-button/ExportPdfModal";
import SafetyDiagReportSection from "../../sections/safety-diag/SafetyDiagReportSection";
import CustomerNoteSticky from "../../../../components/data/customer/CustomerNoteSticky";

// Icônes
import {
  PaperClipIcon,
  HomeIcon,
  ShareIcon,
  BookOpenIcon,
  TruckIcon,
  ScissorsIcon,
  RssIcon,
  ClipboardDocumentListIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/solid";
import { CubeIcon, PaperAirplaneIcon } from "@heroicons/react/24/outline";
import { t } from "i18next";
import { Typography } from "@material-tailwind/react";
import WorkordersStatusDisplay from "../../../../components/data/workorder-status/WorkordersStatusDisplay";
import SuspensionReasonBadge from "../../../../components/data/workorder-status/SuspensionReasonBadge";
import { WorkorderStatus } from "../../../../types/workorder/workorderStatus";
import MachinesTab from "./tabs/MachinesTab";
import HosesTab from "./tabs/HosesTab";
import PartsTab from "./tabs/PartsTab";
import OdooHistoryTab from "./tabs/OdooHistoryTab";
import HistoryTab from "./tabs/HistoryTab";
import { usePhotos } from "../../../../hooks/usePhotos";
import { useSignatures } from "../../../../hooks/useSignatures";
import SendToOdooDialog from "../../../../components/workorders/SendToOdooDialog";
import DestockDialog from "../../../../components/workorders/DestockDialog";
import { OdooHistoryItem } from "../../../../types/odoo/odooHistory";
import { WorkorderHeaderActions } from "../../../../components/workorders/WorkorderHeaderActions";
import {
  OdooTransferEstimate,
  isOdooSendInProgress,
} from "../../../../services/odoo/workorderOdoo";

const ODOO_SEND_POLL_MS = 5000;

// Types
interface WorkorderTabsProps {
  data: EasyTech.Workorder;
}

export function WorkorderTabs({ data }: WorkorderTabsProps) {
  // États
  const [activeTab, setActiveTab] = useState("informations");
  const [searchParams, setSearchParams] = useSearchParams();
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [showSendToOdooModal, setShowSendToOdooModal] = useState(false);
  const [showDestockModal, setShowDestockModal] = useState(false);
  const [destockLoading, setDestockLoading] = useState(false);
  const [sendToOdooLoading, setSendToOdooLoading] = useState(false);
  const [odooTransferEstimateSeconds, setOdooTransferEstimateSeconds] = useState<number | null>(null);
  const [odooHistory, setOdooHistory] = useState<OdooHistoryItem[]>([]);
  const [statusHistoryRefreshKey, setStatusHistoryRefreshKey] = useState(0);

  // Nouvel état pour le workorder actualisable
  const { workorder, updateWorkorder } = useWorkorderContext();

  const fetchOdooHistory = async () => {
    try {
      const data = await api.get(`admin/odoo/history/${workorder?.id}?t=${new Date().getTime()}`);
      if (Array.isArray(data)) {
        setOdooHistory(data.sort((a: any, b: any) => b.created - a.created));
      }
    } catch (error) {
      console.error("Error fetching odoo history:", error);
    }
  };

  useEffect(() => {
    if (workorder?.id) {
      fetchOdooHistory();
    }
  }, [workorder?.id]);

  const rolesFromCookie = getCookie("userRoles") || "";
  const userRoles = rolesFromCookie
    .split(",")
    .filter((role) => role.trim().startsWith("ROLE_"));

  // Utiliser le hook de permissions
  const { canEditField } = useWorkorderPermissions(userRoles, workorder);

  // Hooks pour l'exportation des médias
  const { photos, getDownloadUrl: getPhotoDownloadUrl } = usePhotos({
    workorderId: workorder.id,
    initialPhotos: workorder.photos || [],
  });

  const { signatures, getDownloadUrl: getSignatureDownloadUrl } = useSignatures(
    {
      workorderId: workorder.id,
      initialSignatures: workorder.signatures || [],
    }
  );

  // Vérification des permissions pour chaque onglet
  const canEditMachines = canEditField(WorkorderField.MACHINES);
  const canEditHoses = canEditField(WorkorderField.HOSES);
  const canEditParts = canEditField(WorkorderField.PARTS);

  // Vérifier les permissions pour les photos et signatures (onglet Files)
  const canEditPhotosBefore = canEditField(WorkorderField.PHOTOS_BEFORE);
  const canEditPhotosAfter = canEditField(WorkorderField.PHOTOS_AFTER);
  const canEditSignatureAfter = canEditField(WorkorderField.SIGNATURE_AFTER);
  const canEditContactSignatureAfter = canEditField(
    WorkorderField.CONTACT_SIGNATURE_AFTER
  );

  // Vérifier si l'utilisateur a accès à au moins une des fonctionnalités de l'onglet Files
  const canAccessFiles =
    canEditPhotosBefore ||
    canEditPhotosAfter ||
    canEditSignatureAfter ||
    canEditContactSignatureAfter;

  // Gestionnaire d'événements pour le changement d'onglet
  const handleTabChange = (tabValue: string) => {
    setActiveTab(tabValue);
  };

  const handleStatusChange = async () => {
    try {
      const response = await api.get(`users/workorders/${workorder.id}/extended?t=${new Date().getTime()}`, {
        headers: { "Cache-Control": "no-cache", "Pragma": "no-cache" }
      });
      if (response) {
        const newWorkorder = response as EasyTech.Workorder;
        
        // Préserver l'état de déstockage optimiste si nécessaire
        if (
          workorder?.invoice?.stock?.status === 'SUCCESS' && 
          newWorkorder?.invoice?.stock && 
          newWorkorder.invoice.stock.status !== 'SUCCESS'
        ) {
          newWorkorder.invoice.stock.status = 'SUCCESS';
        }
        
        updateWorkorder(newWorkorder);
      }
    } catch (error) {
      console.error(
        "Erreur lors de la récupération des données mises à jour:",
        error
      );
    } finally {
      setStatusHistoryRefreshKey((key) => key + 1);
    }
  };

  // Fonction pour gérer l'exportation du PDF
  const handleExportPDF = async () => {
    if (
      workorder.status !== WorkorderStatus.VALIDATED &&
      workorder.status !== WorkorderStatus.INVOICED &&
      workorder.status !== WorkorderStatus.PAID &&
      workorder.status !== WorkorderStatus.CLOSED
    ) {
      toast.error(t("workorder:report:noReportAvailable"));
      return;
    }



    setShowPdfModal(true);
  };

  // Fonction pour gérer l'envoi au Odoo
  const handleSendToOdooClick = () => {
    if (workorder.status !== WorkorderStatus.VALIDATED) {
      toast.error(t("workorder:report:cantSendToOdooBeforeValidated"));
      return;
    }
    if (isOdooSendInProgress(odooHistory)) {
      toast.error(t("workorder:odoo:inProgress"));
      return;
    }
    setShowSendToOdooModal(true);
  };

  const confirmSendToOdoo = async () => {
    try {
      setSendToOdooLoading(true);
      // Vérifier si une quotation envoyé a odoo existe déjà 
      const hasExistingQuotation = !!workorder.invoice?.externalId;

      try {
        const estimate = await api.get(`odoo/order/${workorder.id}/transfer-estimate`) as OdooTransferEstimate;
        if (typeof estimate?.estimatedSeconds === "number") {
          setOdooTransferEstimateSeconds(estimate.estimatedSeconds);
        }
      } catch (estimateError) {
        console.error("Erreur lors de l estimation Odoo:", estimateError);
      }

      await api.request("POST", `odoo/order/${workorder.id}`);

      // Afficher le message approprié selon si c'est une création ou une mise à jour
      const messageKey = hasExistingQuotation
        ? "workorder:odoo:quotationUpdated"
        : "workorder:odoo:quotationCreated";
      showOdooSuccessToast(t(messageKey));

      // Recharger le composant WorkorderTabs pour afficher la nouvelle historique
      setShowSendToOdooModal(false); // Fermer la modale après succès
      await handleStatusChange();
    } catch (error) {
      console.error("Erreur lors de l envoi Odoo:", error);
      toast.error(t("workorder:odoo:errorSendToOdoo"), {
        duration: 5000,
      });
      // Fermer la modale en cas d'erreur
      setShowSendToOdooModal(false);
    } finally {
      setSendToOdooLoading(false);
      setOdooTransferEstimateSeconds(null);

      // recharger le composant WorkorderTabs pour afficher la nouvelle historique
      await fetchOdooHistory();


      // Rediriger vers la tab odoo history
      setSearchParams((params) => {
        params.set("tab", "odooHistory");
        return params;
      });
    }
  };

  // Fonction pour destocker l'intervention via Odoo
  const handleDestockClick = () => {
    setShowDestockModal(true);
  };

  const confirmDestock = async () => {
    try {
      setDestockLoading(true);
      await api.request("POST", `odoo/order/destock/${workorder.id}`);
      showOdooSuccessToast(t("workorder:logs:destocked"));

      // Mise à jour optimiste de l'état pour désactiver les boutons immédiatement
      if (workorder.invoice) {
        const updatedWorkorder = {
          ...workorder,
          invoice: {
            ...workorder.invoice,
            stock: {
              ...(workorder.invoice.stock || {}),
              status: 'SUCCESS'
            }
          }
        };
        updateWorkorder(updatedWorkorder as EasyTech.Workorder);
      }

      setShowDestockModal(false);
      await handleStatusChange();
    } catch (error) {
      console.error("Erreur lors du destockage:", error);
      toast.error(t("workorder:status:destock_error"));
      setShowDestockModal(false);
    } finally {
      setDestockLoading(false);
      await fetchOdooHistory();
      setSearchParams((params) => {
        params.set("tab", "odooHistory");
        return params;
      });
    }
  };

  // Un SEND lance depuis un autre onglet ou par un autre planificateur n'apparait nulle part dans
  // `workorder` : seule une ligne d'historique en IN_PROGRESS le signale.
  const odooSendInProgress = isOdooSendInProgress(odooHistory);

  // Une synchro lancee ailleurs ne se termine dans aucun evenement local, et l'historique n'est
  // sinon relu qu'au montage : sans ce sondage le bouton resterait grise jusqu'au prochain
  // rechargement de page. Il ne tourne que pendant la fenetre d'envoi et s'arrete des que la ligne
  // retombe -- succes, erreur, ou peremption des 6 minutes cote isOdooSendInProgress.
  useEffect(() => {
    if (!odooSendInProgress) return;

    const interval = setInterval(fetchOdooHistory, ODOO_SEND_POLL_MS);
    return () => clearInterval(interval);
  }, [odooSendInProgress]);

  // A la retombee, `workorder` est encore celui d'avant l'envoi : il porte le badge et l'invoice,
  // qu'il faut relire pour que le ✅ apparaisse sans rechargement.
  const wasOdooSendInProgress = useRef(false);
  useEffect(() => {
    if (wasOdooSendInProgress.current && !odooSendInProgress) {
      handleStatusChange();
    }
    wasOdooSendInProgress.current = odooSendInProgress;
  }, [odooSendInProgress]);

  // Calcul des états d'erreur pour l'onglet Odoo History
  const stockStatus = workorder.invoice?.stock?.status;
  let isOdooSendError = workorder.invoice?.state === 'error';
  let isOdooDestockError = !!(stockStatus && ['ERROR', 'ERROR_RETRY', 'ERROR_NO_RETRY'].includes(stockStatus));

  // Vérification de l'historique (pour les erreurs non reflétées dans l'état ou inconnues)
  if (!isOdooSendError && !isOdooDestockError && odooHistory.length > 0) {
    const lastItem = odooHistory[0];
    const isLastError = ['ERROR', 'ERROR_RETRY', 'ERROR_NO_RETRY'].includes(lastItem.status);

    if (isLastError) {
      if (lastItem.action === 'SEND') isOdooSendError = true;
      else if (lastItem.action === 'DESTOCK') isOdooDestockError = true;
    }
  }

  const tabs = [
    {
      label: t("workorder:tabs:informations"),
      value: "informations",
      icon: BookOpenIcon,
      Component: <Informations data={workorder} />,
    },
    {
      label: t("workorder:tabs:machines"),
      value: "machines",
      icon: TruckIcon,
      Component: (
        <MachinesTab workorder={workorder} canEdit={canEditMachines} />
      ),
    },
    {
      label: t("workorder:tabs:hoses"),
      value: "hoses",
      icon: ShareIcon,
      Component: <HosesTab workorder={workorder} canEdit={canEditHoses} />,
    },
    {
      label: t("workorder:tabs:parts"),
      value: "parts",
      icon: ScissorsIcon,
      Component: <PartsTab workorder={workorder} canEdit={canEditParts} />,
    },
    {
      label: t("workorder:tabs:files"),
      value: "files",
      icon: PaperClipIcon,
      Component: <Files data={workorder} canEdit={canAccessFiles} />,
    },
    {
      label: t("diagSecu:report:tabLabel", { defaultValue: "Safety diagnosis" }),
      value: "safetyDiag",
      icon: ShieldCheckIcon,
      Component: <SafetyDiagReportSection workOrderId={workorder.id} workOrderRef={workorder.ref} customer={workorder.customer} />,
    },
    {
      label: t("workorder:tabs:statusHistory", { defaultValue: "History" }),
      value: "statusHistory",
      icon: ClipboardDocumentListIcon,
      Component: <HistoryTab workorder={workorder} refreshKey={statusHistoryRefreshKey} />,
    },
    ...(odooHistory.length > 0 ? [{
      label: (
        <div className="flex items-center gap-2">
          <span>{t("workorder:tabs:odooHistory")}</span>
          {isOdooSendError && (
            <div className="bg-red-600 rounded-full w-6 h-6 flex items-center justify-center shadow-sm">
              <PaperAirplaneIcon className="w-4 h-4 text-white -rotate-90" strokeWidth={2.5} />
            </div>
          )}
          {!isOdooSendError && isOdooDestockError && (
            <div className="bg-red-600 rounded-full w-6 h-6 flex items-center justify-center shadow-sm">
              <CubeIcon className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
          )}
        </div>
      ),
      value: "odooHistory",
      icon: (props: any) => <RssIcon {...props} />,
      Component: <OdooHistoryTab workorder={workorder} history={odooHistory} onRefresh={fetchOdooHistory} />,
    }] : []),
  ];

  /**
   * Met à jour le fil d'ariane
   */
  const [breadcrumbPathState, setBreadcrumbPathState] = useState<
    { name: string; path: string }[]
  >([]);
  useEffect(() => {
    if (data) {
      setBreadcrumbPathState([
        { name: t("workorder:title"), path: "/workorders" },
        { name: data.ref, path: "/workorders/" + data.id },
      ]);
    }
  }, [data, setBreadcrumbPathState]);

  return (
    <div className="machines-parts-container">
      {/* TODO: Fil d'ariane refacto dans un un composant */}
      <div className="breadcrumb mb-4 flex items-center gap-2">
        <span className="flex items-center gap-2 text-sm">
          <HomeIcon className="h-4 w-4" />
          {">"}{" "}
        </span>
        {breadcrumbPathState.map((item, index) => (
          <span key={index} className="text-sm">
            {index > 0 && " > "}
            <a href={item.path} className="hover:text-blue-500">
              {item.name}
            </a>
          </span>
        ))}
      </div>

      {/* En-tête du workorder */}
      <div className="workorder-header">
        <div className="workorder-title">
          <Typography variant="h2">
            {t("workorder:title_single", { ref: data.ref })}
          </Typography>
        </div>
        <div className="actions">
          <WorkordersStatusDisplay
            status={workorder.status as WorkorderStatus}
            size="lg"
          />
          <WorkorderHeaderActions
            workorder={workorder}
            onStatusChange={handleStatusChange}
            onExportPDF={handleExportPDF}
            onSendToOdoo={handleSendToOdooClick}
            onDestock={handleDestockClick}
            sendToOdooLoading={sendToOdooLoading}
            sendToOdooInProgress={odooSendInProgress}
            destockLoading={destockLoading}
            isOdooDestockError={isOdooDestockError}
            photos={photos}
            signatures={signatures}
            getPhotoDownloadUrl={getPhotoDownloadUrl}
            getSignatureDownloadUrl={getSignatureDownloadUrl}
          />
        </div>
      </div>

      {/*
        * Why the technician paused, read at the top of the detail page instead of chased down by
        * phone. Renders nothing once the fields are all null (never paused, or resumed already),
        * so it costs nothing on the common path.
        */}
      <SuspensionReasonBadge
        reason={workorder.suspensionReason}
        note={workorder.suspensionNote}
        className="mb-4"
      />

      {/*
        * Note planificateur du client, en post-it sous l'en-tête.
        *
        * Above the tabs rather than inside the Informations tab, so it stays in view while the
        * planner works through the machines or the parts -- an instruction like "ne jamais y aller
        * le vendredi" has to be readable at the moment the dates are changed, not only on the tab
        * where the note happens to be filed. It replaces the pop-up that used to fire here.
        *
        * workorder.customer is a CustomerSimpleResponse, which carries plannerNote -- no request.
        */}
      <CustomerNoteSticky
        note={workorder?.customer?.plannerNote}
        customerName={workorder?.customer?.name}
      />

      {/* Navigation entre les onglets */}
      <TabsNav
        defaultTab={activeTab}
        tabs={tabs}
        data={workorder}
        onTabChange={handleTabChange}
      />

      {/* Modal d'exportation PDF */}
      <ExportPdfModal
        workorderId={workorder.id}
        workorderRef={workorder.ref}
        customer={workorder.customer}
        open={showPdfModal}
        onClose={() => setShowPdfModal(false)}
      />

      {/* Modal de confirmation Send to Odoo */}
      <SendToOdooDialog
        open={showSendToOdooModal}
        workorderRef={workorder.ref}
        onClose={() => setShowSendToOdooModal(false)}
        onConfirm={confirmSendToOdoo}
        loading={sendToOdooLoading}
        estimatedSeconds={odooTransferEstimateSeconds}
      />

      {/* Modal de confirmation Destock */}
      <DestockDialog
        open={showDestockModal}
        workorderRef={workorder.ref}
        onClose={() => setShowDestockModal(false)}
        onConfirm={confirmDestock}
        loading={destockLoading}
      />
    </div>
  );
}
